import { Link } from "react-router-dom";
import { useT } from "../i18n/I18nProvider";
import { ChevronLeft } from "lucide-react";
import {
  ContentSection,
  AutoTOC,
  KeyPointsBlock,
  Sources,
} from "../components/contentTemplate";

const SECTIONS = [
  { id: "quartiers-prises",  title: "Les quartiers les plus prisés de Wrocław" },
  { id: "prix-achat",        title: "Prix de l'immobilier à Wrocław par quartier" },
  { id: "prix-location",     title: "Prix de la location à Wrocław" },
  { id: "investir",          title: "Investir à Wrocław : rendement et perspectives" },
  { id: "acheter-etranger",  title: "Acheter à Wrocław quand on est français, belge ou suisse" },
  { id: "faq",               title: "Questions fréquentes" },
];

const SOURCES = [
  {
    url: "https://adresowo.pl/ceny-mieszkan/dolnoslaskie/wroclaw/stare-miasto-j/",
    label: "adresowo.pl, prix par quartier",
    description: "Prix médians et moyens par quartier sur le marché secondaire, quatrième trimestre 2025.",
  },
  {
    url: "https://tabelaofert.pl/ceny-mieszkan/wroclaw/stare-miasto",
    label: "tabelaofert.pl, Stare Miasto",
    description: "Prix moyen de Stare Miasto à 17 459 zł/m² en avril 2026 et ventilation par nombre de pièces.",
  },
  {
    url: "https://domity.com/ile-kosztuje-mieszkanie-we-wroclawiu-2025/",
    label: "DOMiTY, hiérarchie des quartiers",
    description: "Śródmieście au-dessus de 18 000, Stare Miasto au-dessus de 17 000 et Krzyki sous 15 000 zł/m².",
  },
  {
    url: "https://www.luxuryestate.com/fr/poland/lower-silesian-voivodeship/wroclaw/wroclaw",
    label: "LuxuryEstate, maisons de standing",
    description: "Ołtaszyn et Oporów cités parmi les quartiers de maisons individuelles de standing.",
  },
  {
    url: "https://gazetawroclawska.pl/planujesz-wynajac-mieszkanie-we-wroclawiu-zobacz-ile-to-kosztuje-w-kwietniu-2025/ar/c1p2-27454573",
    label: "Gazeta Wrocławska, loyers avril 2025",
    description: "Loyers par quartier, Stare Miasto environ 5 000 zł pour un deux-pièces, moyenne ville environ 3 350 zł.",
  },
  {
    url: "https://www.expatfocus.com/poland/guide/poland-buying-property",
    label: "Expat Focus / Global Property Guide",
    description: "Rendement locatif brut de Wrocław à 6,47 % en septembre 2025, coûts de transaction, autorisation MSWiA pour les non-UE.",
  },
  {
    url: "https://naszwroclaw.com/ceny-mieszkan-w-poszczegolnych-dzielnicach-wroclawia-w-polowie-czerwca/",
    label: "naszwroclaw.com, rendements",
    description: "Rendements de Krzyki et Fabryczna entre 5,6 et 6,25 %.",
  },
  {
    url: "https://www.globalpropertyguide.com/europe/poland/buying-guide",
    label: "Global Property Guide, guide d'achat",
    description: "Droits d'acquisition identiques pour les ressortissants UE, EEE et suisses.",
  },
  {
    url: "https://www.cyriljarnias.com/immobilier-international/investir-immobilier-pologne/villes-pologne-prix-exploser-2030/",
    label: "Cyril Jarnias, perspectives 2030",
    description: "Hausse annuelle de Wrocław à +9,3 % début 2025 et projection de +10 à +16 % d'ici 2030.",
  },
];

const INTERNAL_LINK = "text-[#0056D2] hover:underline";

export default function HousingImmobilierWroclaw() {
  const { lp } = useT();

  return (
    <article
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16"
      data-testid="housing-immobilier-wroclaw-page"
    >
      <Link
        to={lp("/logement")}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#0056D2] mb-6"
        data-testid="housing-immobilier-wroclaw-back"
      >
        <ChevronLeft className="w-4 h-4" />
        Retour au logement
      </Link>

      {/* Hero */}
      <header className="mb-10" data-testid="housing-immobilier-wroclaw-hero">
        <span className="hej-chip" data-testid="housing-immobilier-wroclaw-chip">
          <span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>
          Logement · Immobilier
        </span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-4">
          Immobilier à Wrocław : quartiers prisés, prix d'achat et de location
        </h1>
      </header>

      {/* Chapeau d'introduction */}
      <section
        className="space-y-4 text-slate-700 leading-relaxed text-lg mb-12"
        data-testid="housing-immobilier-wroclaw-lede"
      >
        <p>
          Wrocław sépare son prestige en deux mondes, le cachet urbain du centre historique et le calme résidentiel des quartiers
          de villas, et le prix au mètre carré suit cette ligne de partage. Cette page compare les quartiers les plus recherchés
          de la ville, à l'achat comme à la location, avec les chiffres de fin 2025 et début 2026, à destination des Français,
          Belges et Suisses qui s'installent ou investissent. Les prix polonais varient d'un portail à l'autre (prix affichés
          contre prix de transaction, neuf contre ancien), les fourchettes ci-dessous donnent donc un ordre de grandeur fiable
          plutôt qu'une valeur au zloty près. Conversion de référence à la mi-2026 : 1 EUR vaut environ 4,30 PLN et 1 CHF environ
          4,70 PLN, ce qui place un montant en francs suisses à peu près 9 % sous son équivalent en euros.
        </p>
      </section>

      {/* Sommaire */}
      <AutoTOC sections={SECTIONS} testid="housing-immobilier-wroclaw-toc" />

      {/* À retenir */}
      <KeyPointsBlock testid="housing-immobilier-wroclaw-key-points">
        <ul className="list-disc pl-5 space-y-2">
          <li>Centre historique (Stare Miasto, Śródmieście) : 16 000 à 18 000 zł/m², les mètres carrés les plus chers et les plus prestigieux.</li>
          <li>Quartiers de villas (Borek, Biskupin, Sępolno, Karłowice) : 13 000 à 16 000 zł/m², prisés des familles et des expatriés.</li>
          <li>Rendement locatif brut autour de 6,5 % à Wrocław, très au-dessus des grandes villes d'Europe de l'Ouest.</li>
          <li>Ressortissants de l'UE, de l'EEE et de la Suisse : achat sans permis, aux mêmes conditions que les citoyens polonais.</li>
        </ul>
      </KeyPointsBlock>

      {/* Sections */}
      <ContentSection id="quartiers-prises" n={1} title="Les quartiers les plus prisés de Wrocław">
        <h3 className="font-display text-xl font-semibold text-slate-900 mt-6 mb-3">Le prestige du centre : Stare Miasto et Śródmieście</h3>
        <p>
          Le haut du marché se joue à Stare Miasto, la Vieille Ville autour du Rynek et d'Ostrów Tumski, et à Śródmieście, le
          péricentre qui court du Plac Grunwaldzki aux bords de l'Oder. Ce sont les mètres carrés les plus chers de Wrocław,
          portés par les immeubles de rapport rénovés, quelques programmes neufs haut de gamme et la proximité immédiate des
          universités, des bureaux et de la vie culturelle. On y achète du cachet et de la liquidité à la revente, avec une
          clientèle locative de cadres et d'expatriés. C'est le choix logique pour qui vise le standing et une location de
          qualité plutôt que le rendement maximal.
        </p>
        <h3 className="font-display text-xl font-semibold text-slate-900 mt-6 mb-3">Les quartiers de villas : Borek, Biskupin, Sępolno, Karłowice</h3>
        <p>
          Le prestige résidentiel se trouve dans une couronne de quartiers verts et pavillonnaires. Au sud, dans l'arrondissement
          de Krzyki, Borek reste le quartier de villas historique, le plus bourgeois, bordé d'arbres et proche du parc
          Południowy. À côté, Oporów, Ołtaszyn, Partynice et Wojszyce forment un tissu de maisons individuelles à faible
          densité, l'hippodrome de Partynice tout près. À l'est, un chapelet de quartiers cité-jardin longe le parc Szczytnicki,
          le zoo et la Hala Stulecia classée à l'UNESCO : Biskupin, Sępolno, Zalesie, Szczytniki et Zacisze. Au nord, Karłowice
          joue le même rôle de quartier-jardin recherché. Ces adresses attirent les familles et les expatriés en quête de vert
          et de maison, là où le centre offre surtout des appartements. Pour préparer votre installation, notre page dédiée au{" "}
          <Link to={lp("/logement")} className={INTERNAL_LINK}>logement à Wrocław</Link> détaille la recherche, le bail et les
          points à surveiller.
        </p>
      </ContentSection>

      <ContentSection id="prix-achat" n={2} title="Prix de l'immobilier à Wrocław par quartier">
        <p>
          Prix au mètre carré sur le marché de la revente, fin 2025 et début 2026, avec l'équivalent en euros et un loyer
          indicatif pour un deux-pièces.
        </p>

        <div className="hej-card p-4 sm:p-6 my-6" data-testid="housing-immobilier-wroclaw-table-wrapper">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="housing-immobilier-wroclaw-table">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left font-display font-semibold text-slate-900 px-3 py-3 align-top">Quartier</th>
                  <th className="text-left font-display font-semibold text-slate-900 px-3 py-3 align-top">Achat (zł/m²)</th>
                  <th className="text-left font-display font-semibold text-slate-900 px-3 py-3 align-top">≈ €/m²</th>
                  <th className="text-left font-display font-semibold text-slate-900 px-3 py-3 align-top">Loyer 2 pièces (zł/mois)</th>
                  <th className="text-left font-display font-semibold text-slate-900 px-3 py-3 align-top">Caractère</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                <tr className="border-b border-slate-200">
                  <td className="px-3 py-3 align-top font-medium text-slate-900">Stare Miasto</td>
                  <td className="px-3 py-3 align-top">17 000 à 17 500</td>
                  <td className="px-3 py-3 align-top">3 950 à 4 070</td>
                  <td className="px-3 py-3 align-top">env. 5 000</td>
                  <td className="px-3 py-3 align-top">Prestige urbain, cachet historique</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="px-3 py-3 align-top font-medium text-slate-900">Śródmieście</td>
                  <td className="px-3 py-3 align-top">16 000 à 18 000</td>
                  <td className="px-3 py-3 align-top">3 720 à 4 190</td>
                  <td className="px-3 py-3 align-top">env. 4 000</td>
                  <td className="px-3 py-3 align-top">Péricentre, universités, bords d'Oder</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="px-3 py-3 align-top font-medium text-slate-900">Borek (Krzyki)</td>
                  <td className="px-3 py-3 align-top">14 000 à 16 000+</td>
                  <td className="px-3 py-3 align-top">3 250 à 3 720+</td>
                  <td className="px-3 py-3 align-top">4 000 à 4 500</td>
                  <td className="px-3 py-3 align-top">Villas anciennes, le plus bourgeois</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="px-3 py-3 align-top font-medium text-slate-900">Krzyki (général)</td>
                  <td className="px-3 py-3 align-top">13 500 à 14 500</td>
                  <td className="px-3 py-3 align-top">3 140 à 3 370</td>
                  <td className="px-3 py-3 align-top">env. 4 200</td>
                  <td className="px-3 py-3 align-top">Vert, bien desservi, valeur sûre</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="px-3 py-3 align-top font-medium text-slate-900">Biskupin / Sępolno / Zacisze</td>
                  <td className="px-3 py-3 align-top">13 000 à 14 000</td>
                  <td className="px-3 py-3 align-top">3 020 à 3 250</td>
                  <td className="px-3 py-3 align-top">3 500 à 4 200</td>
                  <td className="px-3 py-3 align-top">Cité-jardin, familles, parc Szczytnicki</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="px-3 py-3 align-top font-medium text-slate-900">Karłowice</td>
                  <td className="px-3 py-3 align-top">13 000 à 14 000</td>
                  <td className="px-3 py-3 align-top">3 020 à 3 250</td>
                  <td className="px-3 py-3 align-top">3 200 à 3 800</td>
                  <td className="px-3 py-3 align-top">Quartier-jardin nord, calme</td>
                </tr>
                <tr>
                  <td className="px-3 py-3 align-top font-medium text-slate-900">Oporów / Ołtaszyn / Partynice</td>
                  <td className="px-3 py-3 align-top">maisons (vendues au bien)</td>
                  <td className="px-3 py-3 align-top">n. c.</td>
                  <td className="px-3 py-3 align-top">peu de locatif</td>
                  <td className="px-3 py-3 align-top">Pavillonnaire de standing</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <p>
          Un repère de méthode pour ne pas se tromper : quelques micro-secteurs neufs comme Swojczyce affichent ponctuellement
          des prix supérieurs à Borek (17 000 à 18 000 zł/m² sur certaines annonces récentes), effet de programmes haut de gamme
          et non cote durable d'un quartier. Un prix tiré par du neuf ne fait pas le prestige d'une adresse.
        </p>
      </ContentSection>

      <ContentSection id="prix-location" n={3} title="Prix de la location à Wrocław">
        <p>
          Le loyer moyen d'un logement à Wrocław tournait autour de 3 350 zł par mois au printemps 2025, soit près de 780 euros.
          La géographie des prix recoupe celle de l'achat : Stare Miasto est le secteur le plus cher, avec un deux-pièces qui
          atteint 5 000 zł mensuels, Śródmieście se tient autour de 4 000 zł, Krzyki fait office de juste milieu vers 4 200 zł,
          et les quartiers plus excentrés comme Psie Pole ou Fabryczna descendent sous 2 000 zł. Un point que beaucoup
          d'expatriés découvrent trop tard : au loyer affiché s'ajoutent les charges de copropriété (czynsz) et les
          consommations d'eau, d'électricité et de chauffage, rarement incluses, qui alourdissent la facture réelle de
          plusieurs centaines de zlotys. Pour cadrer votre budget global, comparez ces montants avec notre page sur{" "}
          <Link to={lp("/cout-de-la-vie")} className={INTERNAL_LINK}>le coût de la vie à Wrocław</Link>.
        </p>
      </ContentSection>

      <ContentSection id="investir" n={4} title="Investir à Wrocław : rendement et perspectives">
        <p>
          Le rendement est l'argument fort de Wrocław face à l'Europe de l'Ouest. Le rendement locatif brut moyen des
          appartements en Pologne s'établissait à 6,17 % en septembre 2025, avec Wrocław à 6,47 %, très au-dessus des 2 à 4 %
          courants à Paris, Amsterdam ou Vienne. Krzyki et Fabryczna offrent les meilleurs chiffres, entre 5,6 et 6,25 %,
          surtout sur les studios proches des campus.
        </p>
        <p>
          La logique d'arbitrage est simple. Les quartiers de prestige central donnent la meilleure valorisation et la meilleure
          qualité locative, mais un rendement tassé parce que le ticket d'entrée est élevé. Les quartiers verts et pavillonnaires
          se tiennent surtout pour de la résidence principale ou de la location familiale longue durée, pas pour du rendement
          optimisé. Qui cherche le rendement pur descend d'un cran vers Krzyki, Fabryczna ou les petits studios du secteur Plac
          Grunwaldzki. Sur la revente, Wrocław affichait environ +9,3 % de hausse annuelle début 2025, et les projections tablent
          sur +10 à +16 % cumulés d'ici 2030, trajectoire haussière qui se calme après les fortes années 2023 et 2024.
        </p>
      </ContentSection>

      <ContentSection id="acheter-etranger" n={5} title="Acheter à Wrocław quand on est français, belge ou suisse">
        <p>
          La règle de départ vaut pour les trois profils : les ressortissants de l'Union européenne, de l'EEE et de la Suisse
          achètent aux mêmes conditions que les citoyens polonais et n'ont besoin d'aucun permis, y compris pour une maison avec
          terrain. Le Suisse est logé à la même enseigne que le Français ou le Belge. Pour un acheteur hors UE, l'achat d'un
          appartement (lokal) reste libre, mais une maison ou un terrain exige une autorisation du ministère de l'Intérieur
          (MSWiA), procédure de quatre à douze semaines. Le point sensible n'est donc pas le quartier mais la nature du bien.
        </p>
        <p>
          Côté budget, les frais de transaction se situent entre 4 et 7 % du prix. Dans l'ancien, on paie la taxe sur les
          transactions civiles (PCC) de 2 %, alors que le neuf est vendu TVA incluse (8 % sur le résidentiel jusqu'à une certaine
          surface). S'ajoutent les honoraires de notaire et l'inscription au registre foncier. Pour un crédit polonais ou même
          l'ouverture d'un compte, il faut en pratique un numéro PESEL, facile à obtenir pour un ressortissant UE ou suisse. Les
          revenus locatifs sont imposables en Pologne, et le régime forfaitaire (ryczałt) à 8,5 % jusqu'à 100 000 zł de loyers
          annuels puis 12,5 % au-delà est le plus courant pour un bailleur particulier. Deux réflexes protègent l'acheteur
          étranger : vérifier le registre foncier (księga wieczysta) du bien avant tout engagement, et travailler avec un agent
          bilingue qui donne accès aux prix de transaction réels et pas seulement aux prix affichés. Le détail de la procédure
          figure sur notre page{" "}
          <Link to={lp("/acheter-bien-immobilier-pologne")} className={INTERNAL_LINK}>acheter un bien immobilier en Pologne</Link>.
        </p>
      </ContentSection>

      <ContentSection id="faq" n={6} title="Questions fréquentes">
        <h3 className="font-display text-lg font-semibold text-slate-900 mt-4 mb-2">Quels sont les quartiers les plus prisés de Wrocław ?</h3>
        <p>
          Stare Miasto et Śródmieście pour le prestige urbain et les appartements de caractère, Borek, Biskupin, Sępolno et
          Karłowice pour les maisons et l'ambiance de quartier-jardin, Krzyki pour un bon équilibre entre verdure, transports et
          prix.
        </p>
        <h3 className="font-display text-lg font-semibold text-slate-900 mt-5 mb-2">Combien coûte un appartement à Wrocław en 2026 ?</h3>
        <p>
          Comptez environ 13 000 à 14 500 zł/m² dans les quartiers résidentiels recherchés et 16 000 à 18 000 zł/m² dans le
          centre historique, soit à peu près 3 000 à 4 200 euros le mètre carré selon la localisation et l'état du bien.
        </p>
        <h3 className="font-display text-lg font-semibold text-slate-900 mt-5 mb-2">Un Français ou un Belge peut-il acheter un bien immobilier en Pologne ?</h3>
        <p>
          Oui, sans permis particulier. Les ressortissants de l'UE achètent appartements, maisons et terrains aux mêmes
          conditions que les citoyens polonais.
        </p>
        <h3 className="font-display text-lg font-semibold text-slate-900 mt-5 mb-2">Un Suisse peut-il acheter à Wrocław dans les mêmes conditions ?</h3>
        <p>
          Oui. La Suisse est assimilée à l'UE et à l'EEE pour l'acquisition immobilière en Pologne, donc pas de permis à
          demander, même pour une maison avec jardin.
        </p>
        <h3 className="font-display text-lg font-semibold text-slate-900 mt-5 mb-2">Quel est le rendement locatif à Wrocław ?</h3>
        <p>
          Le rendement brut moyen approche 6,5 % à l'échelle de la ville, avec des pointes vers 6 % et plus sur les studios de
          Krzyki et Fabryczna, nettement au-dessus des grandes villes d'Europe de l'Ouest.
        </p>
        <h3 className="font-display text-lg font-semibold text-slate-900 mt-5 mb-2">Quel budget prévoir pour louer à Wrocław ?</h3>
        <p>
          Autour de 3 350 zł par mois en moyenne, un deux-pièces montant à 4 000 zł à Śródmieście et jusqu'à 5 000 zł à Stare
          Miasto, charges et consommations à ajouter au loyer affiché.
        </p>
      </ContentSection>

      {/* Sources */}
      <Sources sources={SOURCES} testid="housing-immobilier-wroclaw-sources" />
    </article>
  );
}
